import axios from '../axios'
import { BOOKROUTES } from 'src/router/Api'
import { BookAudioItem, BookData, ChapterAudioDetail, ChapterData } from 'src/interfaces'

export interface CreateBookUniViniPayload {
	title: string
	language: string[]
	language_type: string
	category: string
	author: string
	summary: string
	level: string
	cover_image?: string
}

export interface UpdateBookUniViniPayload extends CreateBookUniViniPayload {
	deleted_at?: string | null
}

export interface RegenerateChapterAudioPayload {
	action: 'regenerate_audio'
	book_audio_id: string
}

export interface RegenerateChapterTextPayload {
	action: 'regenerate_text'
	book_audio_id: string
}

/** Mobile does not call a remove API — kept for future BE support */
export interface ClearChapterAudioPayload {
	action: 'clear_audio'
	book_audio_id?: string
	language?: string
}

export type EditPublishedChapterPayload =
	| RegenerateChapterAudioPayload
	| RegenerateChapterTextPayload
	| ClearChapterAudioPayload

export const createBookUniVini = async (payload: CreateBookUniViniPayload) => {
	return await axios.post(BOOKROUTES.book, {
		...payload,
		// is_univini: true
	})
}

export const updateBookUniVini = async (
	userBookId: string,
	payload: UpdateBookUniViniPayload,
) => {
	return await axios.put(`${BOOKROUTES.book}/${userBookId}`, payload)
}

export const deleteBookUniVini = async (userBookId: string) => {
	return await axios.delete(`${BOOKROUTES.book}/${userBookId}`)
}

export const getListChapterInprogress = async (params) => {
	return await axios.get(BOOKROUTES.chapterInProgressUser, {
		params: {
			...params,
			status: params.status,
		},
	})
}

export const putUpdateChapterStatus = async (params: {
	id: string
	approved_status: string
}) => {
	return await axios.put(`${BOOKROUTES.chapter}/${params.id}/status`, {
		approved_status: params.approved_status,
	})
}

export const getListBookUniVini = async (params) => {
	return await axios.get(BOOKROUTES.bookUniVini, {
		params: {
			...params,
			status: params.status,
		},
	})
}

export const getChapterDetails = async (chapterId: string) => {
	return await axios.get(BOOKROUTES.chapterDetails(chapterId))
}

export const getUniViniChapters = async (bookId: string) => {
	return await axios.get(BOOKROUTES.univiniChapters(bookId))
}

export const getBookAudioList = async (params: {
	book_id: string
	limit?: number
	offset?: number
}) => {
	return await axios.get(BOOKROUTES.bookAudioList, { params })
}

export const getChapterProcessingStatus = async (chapterId: string) => {
	return await axios.get(BOOKROUTES.chapterStatus(chapterId))
}

export const editPublishedChapter = async (
	chapterId: string,
	payload: EditPublishedChapterPayload,
) => {
	return await axios.put(BOOKROUTES.editPublishedChapter(chapterId), payload)
}

export interface UpdatePublishedChapterPayload {
	title?: string
	summary?: string
	thumbnail?: string | null
	doc_url?: string | null
}

export const updatePublishedChapter = async (
	chapterId: string,
	payload: UpdatePublishedChapterPayload,
) => {
	return await axios.put(BOOKROUTES.updatePublishedChapter(chapterId), payload)
}

export const downloadChapterText = async (
	chapterId: string,
	language: string,
	params?: { upload_to_s3?: boolean },
) => {
	return await axios.get(BOOKROUTES.downloadChapterText(chapterId, language), {
		params: {
			upload_to_s3: true,
			...params,
		},
	})
}

export const unpublishChapter = async (chapterId: string) => {
	return await axios.post(BOOKROUTES.unpublishChapter(chapterId))
}

export const publishChapters = async (ids: string[]) => {
	return await axios.post(BOOKROUTES.publishChapters, { ids })
}

export interface CreatePublishedChapterPayload {
	book_id: string
	english_doc_url: string
	title?: string
	summary?: string
	thumbnail?: string | null
}

export const createPublishedChapter = async (
	payload: CreatePublishedChapterPayload,
) => {
	return await axios.post(BOOKROUTES.createPublishedChapter, payload)
}

const extractRows = (payload: any): any[] => {
	const root = payload?.data ?? payload
	const objects = root?.results?.objects

	if (Array.isArray(objects?.rows)) return objects.rows
	if (Array.isArray(objects)) return objects

	const object = root?.results?.object
	if (Array.isArray(object)) return object
	if (Array.isArray(object?.chapters)) return object.chapters
	if (Array.isArray(object?.rows)) return object.rows

	if (Array.isArray(object?.audios)) return object.audios
	if (Array.isArray(object?.audio_list)) return object.audio_list

	if (Array.isArray(root?.results?.rows)) return root.results.rows
	if (Array.isArray(root?.results)) return root.results
	if (Array.isArray(root?.rows)) return root.rows

	return []
}

export const normalizeBookAudioItem = (item: any): BookAudioItem => {
	const nested = item?.book_audio ?? item?.audio ?? null
	const source = nested && typeof nested === 'object' ? { ...nested, ...item } : item

	const url =
		source?.url ??
		source?.audio_url ??
		source?.file_url ??
		source?.audioUrl ??
		null

	const id =
		source?.book_audio_id ??
		source?.bookAudioId ??
		source?.id ??
		source?.audio_id ??
		''

	return {
		id: String(id || ''),
		chapter_id:
			source?.chapter_id ??
			source?.chapterId ??
			item?.chapter_id ??
			item?.chapterId ??
			null,
		language: source?.language ?? source?.lang ?? source?.language_code ?? item?.language ?? item?.lang ?? null,
		url,
		filename:
			source?.filename ??
			source?.file_name ??
			source?.name ??
			(typeof url === 'string' ? url.split('/').pop() : undefined),
		file_size: source?.file_size ?? source?.size ?? null,
	}
}

export const parseBookAudioList = (response: any): BookAudioItem[] => {
	const root = response?.data ?? response
	let rows = extractRows(root)

	if (!rows.length) {
		const object = root?.results?.object
		if (Array.isArray(object?.list)) rows = object.list
		else if (Array.isArray(object?.items)) rows = object.items
		else if (Array.isArray(object?.audios)) rows = object.audios
	}

	return rows.map(normalizeBookAudioItem).filter((item) => isValidBookAudioItem(item))
}

const isValidBookAudioItem = (item: BookAudioItem) =>
	Boolean(item.id || item.url)

export const extractAudiosFromChapterContent = (
	chapter?: ChapterData | null,
): BookAudioItem[] => {
	if (!chapter?.content?.length) return []

	return chapter.content.flatMap((contentItem) => {
		const language = contentItem.language || 'en'
		if (!Array.isArray(contentItem.audio)) return []

		return contentItem.audio
			.map((audio) =>
				normalizeBookAudioItem({
					...audio,
					language:
						(audio as Record<string, unknown>).language ??
						(audio as Record<string, unknown>).language_type ??
						language,
					chapter_id: chapter.id,
				}),
			)
			.filter((item) => item.id || item.url)
	})
}

export const parseUniViniBookChapters = (response: any): ChapterData[] => {
	return extractRows(response).map((item) => item as ChapterData)
}

export const parseAudioDetails = (items: unknown): ChapterAudioDetail[] => {
	if (!Array.isArray(items)) return []

	return items
		.map((item: any) => ({
			id: String(item?.id ?? ''),
			language: item?.language ?? null,
			language_variant: item?.language_variant ?? item?.languageVariant ?? null,
			url: item?.url ?? item?.audio_url ?? null,
			doc_url: item?.doc_url ?? item?.docUrl ?? null,
			status: item?.status ?? null,
			chapter_id: item?.chapter_id ?? item?.chapterId ?? null,
			alignment: item?.alignment ?? null,
		}))
		.filter((item) => item.id)
}

export const parseChapterDetails = (response: any): ChapterData | null => {
	const payload = response?.data?.results?.object
		?? response?.data?.results
		?? response?.data

	if (!payload || typeof payload !== 'object') return null

	return {
		...(payload as ChapterData),
		id: payload.id ?? payload.chapter_id,
		audio_details: parseAudioDetails(payload.audio_details ?? payload.audioDetails),
	} as ChapterData
}

export const parseChapterStatus = (response: any): ChapterData | null => {
	return parseChapterDetails(response)
}

export const parseUniViniChapter = (
	book: BookData,
	chapterId: string,
	response: any,
	fallback?: ChapterData | null,
): ChapterData | null => {
	const rows = parseUniViniBookChapters(response)
	const matched = rows.find((item) => item?.id === chapterId)
	if (matched) return matched

	const fromBook = book.chapters?.find((item) => item.id === chapterId)
	return (fromBook ?? fallback ?? null) as ChapterData | null
}
